/**
 * SmsDashboardPage — SMS dashboard osszefoglalo, gyors kuldes, utolso uzenetek
 * Epic 67: SMS Gateway & Ketiranyu Protokoll
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSendSms, useSmsHistory, useSmsTemplates } from '@/hooks/useSms';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS: Record<string, string> = {
  SENT: 'text-green-600',
  DELIVERED: 'text-green-700',
  PENDING: 'text-yellow-600',
  FAILED: 'text-red-600',
  REJECTED: 'text-red-700',
};

export function SmsDashboardPage() {
  const { t, locale } = useTranslation();
  const LOCALE_MAP: Record<string, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE', uk: 'uk-UA' };
  const navigate = useNavigate();

  const { result: history, isLoading: historyLoading } = useSmsHistory();
  const { result: templates } = useSmsTemplates();
  const sendSms = useSendSms();

  // Quick send form
  const [sendForm, setSendForm] = useState({ phoneNumber: '', message: '' });
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    if (templateId && templates?.templates) {
      const tpl = templates.templates.find(t => t.id === templateId);
      if (tpl) {
        setSendForm(prev => ({ ...prev, message: tpl.body }));
      }
    }
  };

  const handleSend = async () => {
    if (!sendForm.phoneNumber || !sendForm.message) return;
    await sendSms.mutateAsync({
      phoneNumber: sendForm.phoneNumber,
      message: sendForm.message,
    });
    setSendForm({ phoneNumber: '', message: '' });
    setSelectedTemplate('');
  };

  // Compute summary stats from history
  const stats = useMemo(() => {
    if (!history?.logs) return { sentToday: 0, successRate: 0, pendingFailed: 0 };

    const today = new Date().toISOString().slice(0, 10);
    const todayLogs = history.logs.filter(
      log => log.direction === 'OUTBOUND' && log.createdAt.slice(0, 10) === today
    );

    const sentToday = todayLogs.length;
    const delivered = todayLogs.filter(
      log => log.status === 'DELIVERED' || log.status === 'SENT'
    ).length;
    const successRate = sentToday > 0 ? Math.round((delivered / sentToday) * 100) : 0;
    const pendingFailed = todayLogs.filter(
      log => log.status === 'PENDING' || log.status === 'FAILED' || log.status === 'REJECTED'
    ).length;

    return { sentToday, successRate, pendingFailed };
  }, [history]);

  // Recent 10 logs
  const recentLogs = useMemo(() => {
    if (!history?.logs) return [];
    return history.logs.slice(0, 10);
  }, [history]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('sms.dashboardTitle')}</h1>
        <Button variant="outline" onClick={() => navigate('/settings/sms')}>
          {t('sms.settingsLink')}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{stats.sentToday}</div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1] mt-1">
              {t('sms.sentToday')}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{stats.successRate}%</div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1] mt-1">
              {t('sms.successRate')}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-red-600">{stats.pendingFailed}</div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1] mt-1">
              {t('sms.pendingFailed')}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Send */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('sms.quickSend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                placeholder={t('sms.phonePlaceholder')}
                value={sendForm.phoneNumber}
                onChange={e => setSendForm({ ...sendForm, phoneNumber: e.target.value })}
              />
              {templates?.templates && templates.templates.length > 0 && (
                <select
                  className="border rounded px-3 py-2 bg-background"
                  value={selectedTemplate}
                  onChange={e => handleTemplateSelect(e.target.value)}
                >
                  <option value="">{t('sms.selectTemplate')}</option>
                  {templates.templates
                    .filter(tpl => tpl.active)
                    .map(tpl => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </option>
                    ))}
                </select>
              )}
            </div>
            <textarea
              className="w-full border rounded px-3 py-2 bg-background min-h-[80px]"
              placeholder={t('sms.messagePlaceholder')}
              maxLength={480}
              value={sendForm.message}
              onChange={e => setSendForm({ ...sendForm, message: e.target.value })}
            />
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('sms.charCount', { count: sendForm.message.length })}
              </span>
              <Button onClick={handleSend} disabled={sendSms.isPending}>
                {sendSms.isPending ? t('sms.sending') : t('sms.send')}
              </Button>
            </div>
            {sendSms.isSuccess && <p className="text-green-600 text-sm">{t('sms.sentSuccess')}</p>}
            {sendSms.isError && (
              <p className="text-red-600 text-sm">
                {t('common.error')}: {sendSms.error?.message ?? t('common.unknownError')}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('sms.recentHistory')}</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/settings/sms')}>
              {t('sms.viewAll')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="text-center py-4 text-gray-500 dark:text-[#ccfbf1]">
              {t('common.loading')}
            </div>
          ) : recentLogs.length === 0 ? (
            <p className="text-gray-500 dark:text-[#ccfbf1] text-center py-4">
              {t('sms.noRecords')}
            </p>
          ) : (
            <div className="divide-y">
              {recentLogs.map(log => (
                <div key={log.id} className="py-3 flex justify-between items-center">
                  <div>
                    <span className="font-medium">{log.phoneNumber}</span>
                    <span className="ml-2 text-sm text-gray-500 dark:text-[#ccfbf1]">
                      {log.direction === 'OUTBOUND'
                        ? t('sms.directionOutbound')
                        : log.direction === 'INBOUND'
                          ? t('sms.directionInbound')
                          : log.direction}
                    </span>
                    <span className={`ml-2 text-sm ${STATUS_COLORS[log.status] ?? ''}`}>
                      {log.status}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 dark:text-[#ccfbf1] max-w-xs truncate">
                      {log.message}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-[#99f6e4]">
                      {new Date(log.createdAt).toLocaleString(LOCALE_MAP[locale] ?? 'hu-HU')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
